package com.exura.components.layouts.plan

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import com.exura.components.buttons.AsyncButton
import com.exura.components.dialogs.imageSelection.UserImagesDialogContent
import com.exura.components.icons.InlineIcon
import com.exura.components.icons.UntiledIcon
import com.exura.components.inputs.InlineInputCompactVariant
import com.exura.components.inputs.InlineInputGhostVariant
import com.exura.components.inputs.InlineTextInput
import com.exura.components.inputs.ModeToggleGroup
import com.exura.components.inputs.TextAreaInput
import com.exura.components.layouts.NavigableLayoutScope
import com.exura.components.loading.Loader
import com.exura.components.pageTypography.ListEntryImageStyle
import com.exura.features.localization.commonLocalization
import com.exura.features.plandefinition.domain.model.element.BasePlanElement
import com.exura.features.plandefinition.domain.model.plan.PlanType
import com.exura.features.plandefinition.domain.model.series.SimpleFloatingSeries
import com.exura.features.plandefinition.domain.model.series.SimpleInstructionSeries
import com.exura.features.plandefinition.domain.model.series.SimplePersistentSeries
import com.exura.features.plandefinition.domain.model.series.SimpleQuestionSeries
import com.exura.navigation.Route
import com.exura.navigation.navigateTo
import com.exura.ui.Assets
import com.exura.ui.Dimens
import com.varabyte.kobweb.browser.dom.ElementTarget
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.ObjectFit
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.TextOverflow
import com.varabyte.kobweb.compose.css.WhiteSpace
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxHeight
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxSize
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.flexShrink
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.minHeight
import com.varabyte.kobweb.compose.ui.modifiers.minWidth
import com.varabyte.kobweb.compose.ui.modifiers.objectFit
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.modifiers.overflow
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.textOverflow
import com.varabyte.kobweb.compose.ui.modifiers.whiteSpace
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.core.PageContext
import com.varabyte.kobweb.core.data.addIfAbsent
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.overlay.PopupPlacement
import com.varabyte.kobweb.silk.components.overlay.Tooltip
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.times

@Layout(".components.layouts.NavigableLayout")
@Composable
fun NavigableLayoutScope.PlanConstructorLayout(
    ctx: PageContext,
    content: @Composable PlanConstructorLayoutScope.() -> Unit
) {
    val coroutineScope = rememberCoroutineScope()
    val controller = rememberPlanConstructorLayoutController(ctx)
    val navScope = this

    val sourcePlanId = ctx.route.params[Route.Params.PLAN_ID]
    val sourcePlanTypeString = ctx.route.queryParams[Route.Params.PLAN_TYPE]
    val sourcePlanType = sourcePlanTypeString?.let { PlanType.valueOf(it.uppercase()) }

    LaunchedEffect(sourcePlanId, sourcePlanType) {
        when {
            !sourcePlanId.isNullOrEmpty() -> controller.ensurePlanLoaded(sourcePlanId)
            sourcePlanType != null -> controller.initializeNewPlan(sourcePlanType)
            else -> ctx.navigateTo(Route.DASHBOARD)
        }
    }
    LaunchedEffect(ctx.route.queryParams) {
        controller.updateStateFromUrl(ctx.route.queryParams)
    }

    when (val builder = controller.activePlanBuilder) {
        null -> Loader()
        else -> {
            val state = controller.state
            val scope = remember(builder, state) {
                PlanConstructorLayoutScope(
                    navScope = navScope,
                    planBuilder = builder,
                    layoutMode = state.layoutMode,
                    viewMode = state.viewMode,
                    sideBarElements = state.sideBarItems,
                    controller = controller,
                )
            }

            // LAYOUT UI:
            Column(modifier = Modifier.fillMaxSize().overflow(Overflow.Hidden)) {
                PlanConstructorTopBar(
                    scope = coroutineScope,
                    state = state,
                    handleEvent = controller::handleEvent,
                    onImageBoxClicked = {
                        showDialog {
                            UserImagesDialogContent(
                                initialImageUrl = controller.activePlanBuilder?.imageUrl(),
                                onImageSelected = {
                                    coroutineScope.launch {
                                        controller.handleEvent(PlanConstructorLayoutEvent.UpdateImageSource(it))
                                    }
                                },
                                onDismiss = { closeDialog() },
                            )
                        }
                    },
                )

                Row(modifier = Modifier.weight(1f).minHeight(Dimens.spacingNone).fillMaxWidth()) {
                    PlanConstructorSideBar(
                        modifier = Modifier.weight(2f),
                        state = state,
                        planType = builder.planType,
                        onElementSelected = {
                            coroutineScope.launch {
                                controller.handleEvent(PlanConstructorLayoutEvent.SelectSideBarElement(it))
                            }
                        },
                        onDisplayRangeChange = {
                            coroutineScope.launch {
                                controller.handleEvent(PlanConstructorLayoutEvent.ChangeDisplayRange(it))
                            }
                        },
                        onNavigateBackward = {
                            coroutineScope.launch {
                                controller.handleEvent(PlanConstructorLayoutEvent.NavigateDisplayBackward)
                            }
                        },
                        onNavigateForward = {
                            coroutineScope.launch {
                                controller.handleEvent(PlanConstructorLayoutEvent.NavigateDisplayForward)
                            }
                        },
                        seriesSpawnedElementTooltipContent = { spawned, offset ->
                            controller.getTooltipContentRenderStrategy().invoke(spawned, offset)
                        },
                    )
                    Box(modifier = Modifier.weight(1f).minWidth(Dimens.spacingNone)) {
                        scope.content()
                    }
                }
            }

        }
    }
}

@Composable
private fun PlanConstructorTopBar(
    scope: CoroutineScope,
    state: PlanConstructorLayoutState,
    handleEvent: suspend (PlanConstructorLayoutEvent) -> Unit,
    onImageBoxClicked: () -> Unit,
) {
    Row(
        modifier = PlanConstructorHeaderStyle.toModifier(),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        TopBarModeToggles(
            layoutMode = state.layoutMode,
            onLayoutModeChange = { scope.launch { handleEvent(PlanConstructorLayoutEvent.ChangeLayoutMode(it)) } },
            viewMode = state.viewMode,
            onViewModeChange = { scope.launch { handleEvent(PlanConstructorLayoutEvent.ChangeViewMode(it)) } }
        )

        TopBarPlanDetails(
            modifier = Modifier.weight(1f),
            title = state.title,
            description = state.description,
            imageSrc = state.imageSrc,
            onTitleChange = { scope.launch { handleEvent(PlanConstructorLayoutEvent.UpdatePlanTitle(it)) } },
            onDescriptionChange = { scope.launch { handleEvent(PlanConstructorLayoutEvent.UpdatePlanDescription(it)) } },
            onImageBoxClicked = onImageBoxClicked,
            isReadOnly = state.layoutMode == LayoutMode.VIEW,
        )

        TopBarActionButton(
            layoutMode = state.layoutMode,
            isPlanValid = state.isPlanValid,
            isPlanChanged = state.isPlanChanged,
            onViewModeClicked = { handleEvent(PlanConstructorLayoutEvent.Send) },
            onEditModeClicked = { handleEvent(PlanConstructorLayoutEvent.Save) },
        )
    }
}


@Composable
private fun TopBarModeToggles(
    layoutMode: LayoutMode,
    onLayoutModeChange: (LayoutMode) -> Unit,
    viewMode: ViewMode,
    onViewModeChange: (ViewMode) -> Unit,
) {
    Box(
        modifier = PlanConstructorHeaderElementStyle
            .toModifier(PlanConstructorHeaderElementBorderedVariant)
            .flexShrink(0),
        contentAlignment = Alignment.Center,
    ) {
        Column(
            verticalArrangement = Arrangement.spacedBy(Dimens.planConstructorHeaderElementGap),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            ModeToggleGroup(
                selectedMode = viewMode,
                modes = VIEW_MODES_ITEMS,
                onModeChange = onViewModeChange,
                modifier = Modifier.fillMaxWidth(),
            )
            ModeToggleGroup(
                selectedMode = layoutMode,
                modes = LAYOUT_MODES_ITEMS,
                onModeChange = onLayoutModeChange,
                modifier = Modifier.fillMaxWidth(),
            )
        }
    }
}

@Composable
private fun TopBarPlanDetails(
    modifier: Modifier = Modifier,
    title: String,
    onTitleChange: (String) -> Unit,
    description: String,
    onDescriptionChange: (String) -> Unit,
    imageSrc: String?,
    onImageBoxClicked: () -> Unit,
    isReadOnly: Boolean,
) {
    Row(
        modifier = PlanConstructorHeaderElementStyle
            .toModifier(PlanConstructorHeaderElementBorderedVariant)
            .then(modifier),
        horizontalArrangement = Arrangement.spacedBy(2 * Dimens.planConstructorHeaderElementGap)
    ) {
        Box(
            modifier = ListEntryImageStyle
                .toModifier()
                .let { mod ->
                    if (isReadOnly) mod
                    else mod
                        .cursor(Cursor.Pointer)
                        .onClick { onImageBoxClicked() }
                },
            contentAlignment = Alignment.Center,
        ) {
            if (imageSrc != null) {
                Image(imageSrc, modifier = Modifier.objectFit(ObjectFit.Contain).fillMaxSize())
            } else {
                if (!isReadOnly) {
                    Column(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalAlignment = Alignment.CenterHorizontally,
                    ) {
                        UntiledIcon(
                            Assets.IconsUntiled.GENERAL_ADD,
                            modifier = Modifier.fontSize(Dimens.mainIconSizeDefault),
                        )
                        SpanText(commonLocalization.current.controls.addImage)
                    }
                }
            }
        }
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .fillMaxHeight(),
            verticalArrangement = Arrangement.spacedBy(Dimens.planConstructorHeaderElementGap),
        ) {
            InlineTextInput(
                value = title,
                onValueChange = onTitleChange,
                variant = InlineInputGhostVariant.then(InlineInputCompactVariant),
                modifier = Modifier.fontSize(1.1.cssRem),
                isReadOnly = isReadOnly,
            )
            TextAreaInput(
                value = description,
                onValueChange = onDescriptionChange,
                variant = InlineInputGhostVariant.then(InlineInputCompactVariant),
                modifier = Modifier.fillMaxHeight(),
                isReadOnly = isReadOnly,
            )
        }
    }
}


@Composable
private fun TopBarActionButton(
    layoutMode: LayoutMode,
    isPlanValid: Boolean,
    isPlanChanged: Boolean,
    onViewModeClicked: suspend () -> Unit,
    onEditModeClicked: suspend () -> Unit,
) {
    Box(
        modifier = PlanConstructorHeaderElementStyle
            .toModifier()
            .flexShrink(0),
        contentAlignment = Alignment.Center,
    ) {
        AsyncButton(
            modifier = Modifier.minWidth(Dimens.planConstructorActionButtonMinWidth),
            text =
                if (layoutMode == LayoutMode.VIEW) commonLocalization.current.crud.send
                else commonLocalization.current.crud.save,
            onClick = {
                if (layoutMode == LayoutMode.VIEW) onViewModeClicked()
                else onEditModeClicked()
            },
            icon = {
                if (layoutMode == LayoutMode.VIEW) UntiledIcon(Assets.IconsUntiled.PROCEED)
                else UntiledIcon(Assets.IconsUntiled.SAVE)
            },
            isDisabled =
                if (layoutMode == LayoutMode.VIEW) !isPlanValid
                else !isPlanValid || !isPlanChanged,
        )
    }
}


@Composable
private fun PlanConstructorSideBar(
    modifier: Modifier,
    state: PlanConstructorLayoutState,
    planType: PlanType,
    onElementSelected: (SideBarPlanElement) -> Unit,
    onDisplayRangeChange: (DisplayRange) -> Unit,
    onNavigateBackward: () -> Unit,
    onNavigateForward: () -> Unit,
    seriesSpawnedElementTooltipContent: @Composable (BasePlanElement, Int) -> Unit,
) {
    val isExpanded = (state.viewMode == ViewMode.GRAPHICS) && !state.isLoadingPlanContent

    Column(
        modifier = SideBarStyle.toModifier(
            if (!isExpanded) SideBarCompactVariant else null
        ).then(modifier)
    ) {

        PlanConstructorSideBarHeader(
            isExpanded = isExpanded,
            displayConfig = state.displayConfig,
            onDisplayRangeChange = onDisplayRangeChange,
            onNavigateBackward = onNavigateBackward,
            onNavigateForward = onNavigateForward,
        )
        Column(
            modifier = Modifier
                .weight(1f)
                .minHeight(Dimens.spacingNone)
                .fillMaxWidth()
                .overflow(Overflow.Auto)
        ) {
            when {
                state.isLoadingPlanContent -> Loader()
                else -> state.sideBarItems.forEachIndexed { index, element ->
                    PlanConstructorSideBarRow(
                        element = element,
                        isLast = index == state.sideBarItems.lastIndex,
                        isSelected = element.seriesPos == state.selectedSeriesPos
                                && element.collectionPos == state.selectedCollectionPos,
                        onClick = { onElementSelected(element) },
                        isExpanded = isExpanded,
                        planType = planType,
                        displayConfig = state.displayConfig,
                        seriesSpawnedElementTooltipContent = seriesSpawnedElementTooltipContent,
                    )
                }
            }
        }
    }
}


@Composable
private fun PlanConstructorSideBarHeader(
    isExpanded: Boolean,
    displayConfig: DisplayConfig,
    onDisplayRangeChange: (DisplayRange) -> Unit,
    onNavigateBackward: () -> Unit,
    onNavigateForward: () -> Unit,
) {
    Row(
        modifier = SideBarRowStyle.toModifier(SideBarRowSeparatedThickVariant),
    ) {
        Box(
            modifier = SideBarRowContentStyle.toModifier(
                SideBarRowContentHeaderHeightVariant,
                SideBarRowContentListVariant,
            ),
            contentAlignment = Alignment.Center
        ) {
            SpanText("Zawartość")
        }
        Column(
            modifier = SideBarRowContentStyle.toModifier(
                if (isExpanded) SideBarRowContentGanttVariant else SideBarRowContentHiddenVariant,
                SideBarRowContentHeaderHeightVariant,
            ),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.SpaceBetween,
        ) {
            ModeToggleGroup(
                selectedMode = displayConfig.range,
                modes = DISPLAY_RANGE_OPTIONS,
                onModeChange = onDisplayRangeChange,
                modifier = Modifier,
            )
            SideBarHeaderTimelineNavigation(
                displayConfig = displayConfig,
                chevronLeftClicked = onNavigateBackward,
                chevronRightClicked = onNavigateForward,
            )
        }
    }
}

@Composable
fun SideBarHeaderTimelineNavigation(
    displayConfig: DisplayConfig,
    chevronLeftClicked: () -> Unit,
    chevronRightClicked: () -> Unit,
    ) {
    val offset = displayConfig.offsetFromDayZero
    val navModifier = Modifier
        .fillMaxWidth()
        .fillMaxHeight()
        .minWidth(Dimens.planConstructorSideBarElementIndent)
        .width(Dimens.planConstructorSideBarElementIndent)
    Row(modifier = Modifier.fillMaxWidth()) {
        Box(
            modifier = navModifier,
            contentAlignment = Alignment.Center
        ) {
            UntiledIcon(
                Assets.IconsUntiled.GENERAL_CHEVRON_LEFT,
                modifier = Modifier
                    .cursor(Cursor.Pointer)
                    .onClick { chevronLeftClicked() }
            )
        }

        (0 until displayConfig.cellCount).forEach { cellIndex ->
            Box(
                modifier = Modifier.weight(1f).fillMaxWidth().padding(topBottom = 0.5.cssRem),
                contentAlignment = Alignment.BottomCenter,
            ) {
                val cellSizeInt = when(displayConfig.cellSize) {
                    CellSize.DAY -> 1
                    CellSize.WEEK -> 7
                    CellSize.MONTH -> 30
                }
                val startDay = offset + cellIndex * cellSizeInt
                val endDay = startDay + cellSizeInt - 1
                SpanText(
                    text = if (startDay == endDay) "D$startDay" else "D$startDay-$endDay",
                    modifier = Modifier.fontSize(0.75.cssRem)
                )
            }
        }

        Box(
            modifier = navModifier,
            contentAlignment = Alignment.Center
        ) {
            UntiledIcon(
                Assets.IconsUntiled.GENERAL_CHEVRON_RIGHT,
                modifier = Modifier
                    .cursor(Cursor.Pointer)
                    .onClick { chevronRightClicked() }
            )
        }
    }
}

@Composable
private fun PlanConstructorSideBarRow(
    element: SideBarPlanElement,
    isLast: Boolean,
    isSelected: Boolean,
    onClick: () -> Unit,
    isExpanded: Boolean,
    planType: PlanType,
    displayConfig: DisplayConfig,
    seriesSpawnedElementTooltipContent: @Composable (BasePlanElement, Int) -> Unit,
) {
    val series = when (element) {
        is SideBarPlanElement.CollectionItem -> null
        is SideBarPlanElement.RootSeriesItem -> element.seriesItem
        is SideBarPlanElement.NestedSeriesItem -> element.seriesItem
    }
    val colorVariant = when (series) {
        is SimpleFloatingSeries -> SideBarRowTealVariant
        is SimpleInstructionSeries -> SideBarRowOrangeVariant
        is SimplePersistentSeries -> SideBarRowRedVariant
        is SimpleQuestionSeries -> SideBarRowBlueVariant
        else -> null
    }
    Row(
        modifier = SideBarRowStyle
            .toModifier(
                SideBarRowHoverableVariant,
                colorVariant,
                if (!isLast) SideBarRowSeparatedVariant else null,
                if (isSelected) SideBarRowSelectedVariant else null,
            ),
        verticalAlignment = Alignment.CenterVertically
    ) {
        SeriesListEntry(
            element = element,
            onClick = onClick,
        )

        GanttChartEntry(
            element = element,
            isExpanded = isExpanded,
            planType = planType,
            displayConfig = displayConfig,
            seriesSpawnedElementTooltipContent = seriesSpawnedElementTooltipContent,
        )
    }
}

@Composable
private fun SeriesListEntry(
    element: SideBarPlanElement,
    onClick: () -> Unit,
) {
    val series = when (element) {
        is SideBarPlanElement.RootSeriesItem -> element.seriesItem
        is SideBarPlanElement.NestedSeriesItem -> element.seriesItem
        else -> null
    }
//    if (series is SimpleQuestionSeries) {
//        println(element.renderingData.spawnedSeriesElements)
//    }
    Row(
        modifier = SideBarRowContentStyle.toModifier(
            SideBarRowContentListVariant,
            when (element) {
                is SideBarPlanElement.CollectionItem -> SideBarRowContentCollectionHeightVariant
                is SideBarPlanElement.RootSeriesItem -> SideBarRowContentSeriesHeightVariant
                is SideBarPlanElement.NestedSeriesItem -> SideBarRowContentSeriesHeightVariant
                    .then(SideBarRowContentNestedVariant)
                    .then(SideBarRowContentIndentVariant)
            }
        )
            .cursor(Cursor.Pointer)
            .onClick { onClick() },
        verticalAlignment = Alignment.CenterVertically
    ) {
        InlineIcon(
            icon = {
                if (series != null) {
                    UntiledIcon(
                        when (series) {
                            is SimpleFloatingSeries -> Assets.IconsUntiled.FLOATING
                            is SimpleInstructionSeries -> Assets.IconsUntiled.INSTRUCTION
                            is SimplePersistentSeries -> Assets.IconsUntiled.PERSISTENT
                            is SimpleQuestionSeries -> Assets.IconsUntiled.QUESTION
                            else -> Assets.IconsUntiled.PLACEHOLDER
                        },
                        modifier = constructorSeriesIconStyle.toModifier(),
                    )
                }
            },
            modifier = Modifier.fillMaxWidth(),
        ) {
            SpanText(
                text = element.title,
                modifier = Modifier
                    .overflow(Overflow.Hidden)
                    .textOverflow(TextOverflow.Ellipsis)
                    .whiteSpace(WhiteSpace.NoWrap)
            )
        }
    }
}

@Composable
private fun GanttChartEntry(
    element: SideBarPlanElement,
    isExpanded: Boolean,
    planType: PlanType,
    displayConfig: DisplayConfig,
    seriesSpawnedElementTooltipContent: @Composable (BasePlanElement, Int) -> Unit,
) {
    Row(
        modifier = SideBarRowContentStyle.toModifier(
            if (isExpanded) SideBarRowContentGanttVariant
            else SideBarRowContentHiddenVariant,
            when (element) {
                is SideBarPlanElement.CollectionItem -> SideBarRowContentCollectionHeightVariant
                is SideBarPlanElement.RootSeriesItem -> SideBarRowContentSeriesHeightVariant
                is SideBarPlanElement.NestedSeriesItem -> SideBarRowContentSeriesHeightVariant
                    .then(SideBarRowContentNestedVariant)
            }
        ),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        when (displayConfig.cellSize) {
            CellSize.DAY -> {
                val (displayDayLow, displayDayHigh) = displayConfig.getDisplayDayRange()
                (displayDayLow..displayDayHigh).forEach { day ->
                    GanttChartElementDayBox(
                        renderingData = element.renderingData,
                        day = day,
                        modifier = Modifier.weight(1f),
                        planType = planType,
                        isLastBox = day == displayDayHigh,
                        isHoverable = element !is SideBarPlanElement.CollectionItem,
                        seriesSpawnedElementTooltipContent = seriesSpawnedElementTooltipContent,
                    )
                }
            }

            CellSize.WEEK -> {
                val startDay = displayConfig.offsetFromDayZero
                (0 until displayConfig.cellCount).forEach { weekIndex ->
                    val weekStartDay = startDay + (weekIndex * 7)
                    val weekEndDay = weekStartDay + 6
                    GanttChartElementWeekBox(
                        renderingData = element.renderingData,
                        weekStartDay = weekStartDay,
                        weekEndDay = weekEndDay,
                        modifier = Modifier.weight(1f),
                        planType = planType,
                        isLastBox = weekIndex == displayConfig.cellCount - 1,
                        isHoverable = element !is SideBarPlanElement.CollectionItem,
                        seriesSpawnedElementTooltipContent = seriesSpawnedElementTooltipContent,
                    )
                }
            }

            CellSize.MONTH -> {
                val startDay = displayConfig.offsetFromDayZero
                (0 until displayConfig.cellCount).forEach { monthIndex ->
                    val monthStartDay = startDay + (monthIndex * 30)
                    val monthEndDay = monthStartDay + 29
                    GanttChartElementMonthBox(
                        renderingData = element.renderingData,
                        monthStartDay = monthStartDay,
                        monthEndDay = monthEndDay,
                        modifier = Modifier.weight(1f),
                        planType = planType,
                        isLastBox = monthIndex == displayConfig.cellCount - 1,
                        isHoverable = element !is SideBarPlanElement.CollectionItem,
                        seriesSpawnedElementTooltipContent = seriesSpawnedElementTooltipContent,
                    )
                }
            }
        }
    }
}


@Composable
private fun GanttChartElementDayBox(
    renderingData: SideBarElementRenderingData,
    day: Int,
    modifier: Modifier,
    planType: PlanType,
    isLastBox: Boolean,
    isHoverable: Boolean,
    seriesSpawnedElementTooltipContent: @Composable (BasePlanElement, Int) -> Unit,
) {
    val isStart = day == renderingData.lowDay
    val isMiddle = day >= (renderingData.lowDay ?: Int.MIN_VALUE)
            && day <= (renderingData.highDay ?: Int.MAX_VALUE)
    val isEnd = day == renderingData.highDay
    val spawnElement = renderingData.spawnedSeriesElements.firstOrNull {
        day == it.relativeDay?.plus(renderingData.offset)
    }
    Box(
        modifier = GantDayBoxStyle
            .toModifier(
                if (!isMiddle || isStart) GantDayBoxStartingVariant else null,
                if (isLastBox) GantDayBoxEndingVariant else null,
                if (day == 0)
                    when (planType) {
                        PlanType.PREPLAN -> GantDayBoxPlanAnchorEndVariant
                        PlanType.POSTPLAN -> GantDayBoxPlanAnchorStartVariant
                    }
                else null
            )
            .then(modifier)
    ) {
        GanttChartElementDayLineElement(
            isStart = isStart,
            isMiddle = isMiddle,
            isEnd = isEnd,
            isHoverable = isHoverable,
        ) {
            spawnElement?.let {
                Box(DayBoxSeriesElementStyle.toModifier())
                Tooltip(
                    target = ElementTarget.Parent,
                    placement = PopupPlacement.Top,
                    modifier = SpawnedElementTooltipStyle.toModifier(),
                ) {
                    seriesSpawnedElementTooltipContent(spawnElement, renderingData.offset)
                }
            }
        }
    }
}

@Composable
private fun GanttChartElementDayLineElement(
    isStart: Boolean,
    isMiddle: Boolean,
    isEnd: Boolean,
    isHoverable: Boolean,
    content: @Composable () -> Unit = { },
) {
    Box(
        modifier = PlanConstructorSideBarDayBoxStyle.toModifier(
            if (isMiddle) PlanConstructorSideBarDayBoxMiddleVariant
                .then(
                    if (isHoverable) PlanConstructorSideBarDayBoxHoverableVariant
                    else PlanConstructorSideBarDayBoxFilledVariant
                )
            else null,
            if (isStart && isEnd) PlanConstructorSideBarDayBoxSingleDayVariant
            else if (isStart) PlanConstructorSideBarDayBoxStartVariant
            else if (isEnd) PlanConstructorSideBarDayBoxEndVariant
            else null,
        ),
        contentAlignment = Alignment.Center,
    ) {
        content()
    }
}

@Composable
private fun GanttChartElementWeekBox(
    renderingData: SideBarElementRenderingData,
    weekStartDay: Int,
    weekEndDay: Int,
    modifier: Modifier,
    planType: PlanType,
    isLastBox: Boolean,
    isHoverable: Boolean,
    seriesSpawnedElementTooltipContent: @Composable (BasePlanElement, Int) -> Unit,
) {
    // Check if any day in this week overlaps with the series
    val lowDay = renderingData.lowDay
    val highDay = renderingData.highDay

    val isStart = lowDay != null && lowDay in weekStartDay..weekEndDay
    val isMiddle = (lowDay == null || lowDay <= weekEndDay) && (highDay == null || highDay >= weekStartDay)
    val isEnd = highDay != null && highDay in weekStartDay..weekEndDay

    // Check if any spawned elements fall in this week
    val spawnedInWeek = renderingData.spawnedSeriesElements.filter { element ->
        val elementDay = element.relativeDay?.plus(renderingData.offset)
        elementDay != null && elementDay in weekStartDay..weekEndDay
    }

    val spawnElement = renderingData.spawnedSeriesElements.firstOrNull {
        val elDay = it.relativeDay?.plus(renderingData.offset)
        elDay != null && elDay in weekStartDay..weekEndDay
    }

    Box(
        modifier = GantDayBoxStyle
            .toModifier(
                if (!isMiddle || isStart) GantDayBoxStartingVariant else null,
                if (isLastBox) GantDayBoxEndingVariant else null,
                if (0 in weekStartDay..weekEndDay)
                    when (planType) {
                        PlanType.PREPLAN -> GantDayBoxPlanAnchorEndVariant
                        PlanType.POSTPLAN -> GantDayBoxPlanAnchorStartVariant
                    }
                else null
            )
            .then(modifier)
    ) {
        GanttChartElementDayLineElement(
            isStart = isStart,
            isMiddle = isMiddle,
            isEnd = isEnd,
            isHoverable = isHoverable,
        ) {
            spawnElement?.let {
                Box(DayBoxSeriesElementStyle.toModifier())
                Tooltip(
                    target = ElementTarget.Parent,
                    placement = PopupPlacement.Top,
                    modifier = SpawnedElementTooltipStyle.toModifier(),
                ) {
                    seriesSpawnedElementTooltipContent(spawnElement, renderingData.offset)
                }
            }
        }
    }
}

@Composable
private fun GanttChartElementMonthBox(
    renderingData: SideBarElementRenderingData,
    monthStartDay: Int,
    monthEndDay: Int,
    modifier: Modifier,
    planType: PlanType,
    isLastBox: Boolean,
    isHoverable: Boolean,
    seriesSpawnedElementTooltipContent: @Composable (BasePlanElement, Int) -> Unit,
) {
    // Check if any day in this month overlaps with the series
    val lowDay = renderingData.lowDay
    val highDay = renderingData.highDay

    val isStart = lowDay != null && lowDay in monthStartDay..monthEndDay
    val isMiddle = (lowDay == null || lowDay <= monthEndDay) && (highDay == null || highDay >= monthStartDay)
    val isEnd = highDay != null && highDay in monthStartDay..monthEndDay

    // Check if any spawned elements fall in this month
    val spawnedInMonth = renderingData.spawnedSeriesElements.filter { element ->
        val elementDay = element.relativeDay?.plus(renderingData.offset)
        elementDay != null && elementDay in monthStartDay..monthEndDay
    }
    val spawnElement = renderingData.spawnedSeriesElements.firstOrNull {
        val elDay = it.relativeDay?.plus(renderingData.offset)
        elDay != null && elDay in monthStartDay..monthEndDay
    }

    Box(
        modifier = GantDayBoxStyle
            .toModifier(
                if (!isMiddle || isStart) GantDayBoxStartingVariant else null,
                if (isLastBox) GantDayBoxEndingVariant else null,
                if (0 in monthStartDay..monthEndDay)
                    when (planType) {
                        PlanType.PREPLAN -> GantDayBoxPlanAnchorEndVariant
                        PlanType.POSTPLAN -> GantDayBoxPlanAnchorStartVariant
                    }
                else null
            )
            .then(modifier)
    ) {
        GanttChartElementDayLineElement(
            isStart = isStart,
            isMiddle = isMiddle,
            isEnd = isEnd,
            isHoverable = isHoverable,
        ) {
            spawnElement?.let {
                Box(DayBoxSeriesElementStyle.toModifier())
                Tooltip(
                    target = ElementTarget.Parent,
                    placement = PopupPlacement.Top,
                    modifier = SpawnedElementTooltipStyle.toModifier(),
                ) {
                    seriesSpawnedElementTooltipContent(spawnElement, renderingData.offset)
                }
            }
        }
    }
}
